/**
 * @module agent/adapters/services/ResponseHandler
 *
 * Handles streaming response processing and final content extraction.
 * Extracted from AgentCoreAdapter to reduce adapter complexity.
 *
 * Responsibilities:
 * - Process streaming responses from AgentCore
 * - Extract final response content from accumulated JSON chunks
 * - Delegate tool detection and search tag handling to callbacks
 * - Track streaming state (chunk count, accumulation)
 *
 * Uses callback injection for adapter integration:
 * - handleSearchTags: Delegates to SearchPollCoordinator
 * - removeSearchTags: Cleans tags from content
 * - detectTool: Delegates to ToolDetectionService
 * - onToolDetected: Notifies adapter of tool execution
 * - onChunk: Optional callback for each processed chunk
 */

import { parseChunk } from './StreamingService';
import { SEARCH_TAG_REASONS } from './SearchWorkflowService';

/**
 * Handles streaming response processing.
 */
class ResponseHandler {
  /**
   * @param {Object} callbacks - Callback functions for adapter integration
   * @param {Function} callbacks.handleSearchTags - Called with (content, sessionId) to handle search tags (REQUIRED)
   * @param {Function} callbacks.removeSearchTags - Called with (content) to clean search tags (REQUIRED)
   * @param {Function} [callbacks.detectTool] - Called with (chunk) to detect tool execution
   * @param {Function} [callbacks.onToolDetected] - Called when tool execution is detected
   * @param {Function} [callbacks.onChunk] - Called for each processed chunk
   * @throws {TypeError} If callbacks is not an object or required callbacks are missing
   */
  constructor(callbacks) {
    if (!callbacks || typeof callbacks !== 'object') {
      throw new TypeError('ResponseHandler: callbacks must be an object');
    }

    // Fix #8: Require critical callbacks to prevent silent integration bugs
    if (typeof callbacks.handleSearchTags !== 'function') {
      throw new TypeError('ResponseHandler: handleSearchTags callback is required');
    }
    if (typeof callbacks.removeSearchTags !== 'function') {
      throw new TypeError('ResponseHandler: removeSearchTags callback is required');
    }

    this._callbacks = callbacks;
  }

  /**
   * Fix #3: Parse accumulated content from various JSON structures.
   * Extracted for single responsibility - handles only JSON parsing.
   *
   * @param {string} raw - Raw accumulated content
   * @returns {string} Extracted text content
   * @private
   */
  _parseAccumulatedContent(raw) {
    // Fast path: plain text from SSE streaming (not JSON)
    if (!raw || (!raw.startsWith('{') && !raw.startsWith('['))) {
      return raw;
    }

    try {
      const parsed = JSON.parse(raw);

      // AgentCore format: { result: { content: [{ text: "..." }] } }
      if (Array.isArray(parsed.result?.content)) {
        return parsed.result.content
          .filter((item) => item.text)
          .map((item) => item.text)
          .join('\n\n');
      }

      // Fallback formats: { response: "..." } or { output: "..." }
      return parsed.response || parsed.output || raw;
    } catch {
      // Not JSON - use raw content
      return raw;
    }
  }

  /**
   * Extract final response content from accumulated chunks.
   * Handles multiple JSON structures from AgentCore.
   *
   * @param {string} accumulatedContent - Raw accumulated response content
   * @param {string} sessionId - Session ID for search tag context
   * @param {{pollSearchId?: string}} [options] - Optional poll correlation (TD-2952)
   * @returns {Object} { content: string, isInternalOnly: boolean, reason: string }
   * Fix #9: Renamed from shouldHide to isInternalOnly to clarify purpose -
   * indicates if response is for internal operations (e.g., search polling) vs user-visible.
   */
  extractFinalResponse(accumulatedContent, sessionId, options = {}) {
    // Fix #3: Delegate JSON parsing to separate method
    const contentToAnalyze = this._parseAccumulatedContent(accumulatedContent);

    // Delegate search tag handling to callback (pollSearchId routes concurrent searches)
    const tagResult = this._callbacks.handleSearchTags(contentToAnalyze, sessionId, options);

    // Fix #19: Defensive validation of callback return shape
    // Ensures contract alignment between SearchPollCoordinator and ResponseHandler
    if (!tagResult || typeof tagResult.isInternalOnly !== 'boolean') {
      console.error(
        '❌ ResponseHandler: handleSearchTags must return { isInternalOnly: boolean, reason: string }. Received:',
        tagResult
      );
      // Use safe fallback to prevent breaking message flow
      return {
        content: contentToAnalyze || 'Agent response completed.',
        isInternalOnly: false,
        reason: SEARCH_TAG_REASONS.VALIDATION_ERROR
      };
    }

    // TD-2293: extract enrichment cards (options / quote / discovery / result /
    // job) from the RAW content — parsed here, not in the adapter's onComplete
    // which only receives cleaned content. Optional callback; null when absent.
    // Parsed BEFORE the content fallback below, which depends on it.
    const enrichment = this._callbacks.parseEnrichmentTags?.(contentToAnalyze) ?? null;

    // Remove search tags from displayed content.
    // TD-2293: '' is a LEGITIMATE strip result (a tag-only turn, e.g. the
    // harness-generated '[REVEAL_STARTED:<jobId>]' confirm turn) — only a
    // null/undefined callback result falls back to the raw content. Using ||
    // here would resurrect the raw tags into a visible bubble.
    const strippedContent = this._callbacks.removeSearchTags(contentToAnalyze);
    const cleanedContent = strippedContent ?? contentToAnalyze;
    // A turn whose only payload was an enrichment tag renders from
    // message.enrichment — the card IS the content, so '' is the correct
    // display value. Substituting the placeholder here would paint a stray
    // "Agent response completed." bubble above the card (the turn is
    // isInternalOnly: false, so it is rendered, not hidden). The placeholder
    // still applies to a genuinely empty non-enrichment response.
    const finalContent = cleanedContent || (enrichment ? '' : 'Agent response completed.');

    return {
      content: finalContent,
      isInternalOnly: tagResult.isInternalOnly,
      reason: tagResult.reason,
      enrichment
    };
  }

  /**
   * Process a streaming response from AgentCore.
   * Accumulates chunks, detects tools, and returns final content.
   *
   * @param {AsyncIterable} stream - The response stream to process
   * @param {string} sessionId - Session ID for context
   * @returns {Promise<Object>} { accumulatedContent, chunkCount, toolDetected }
   */
  async processStreamingResponse(stream, sessionId) {
    let accumulatedContent = '';
    let chunkCount = 0;
    let toolDetected = false;

    for await (const chunkEvent of stream) {
      chunkCount++;

      // Parse chunk using StreamingService
      const decodedChunk = parseChunk(chunkEvent);
      accumulatedContent += decodedChunk;

      // Fix #5: Wrap callback in try-catch to ensure stream processing continues
      // even if callback throws - content accumulation is more important
      try {
        this._callbacks.onChunk?.(decodedChunk, accumulatedContent);
      } catch (callbackError) {
        console.warn('⚠️ ResponseHandler: onChunk callback error:', callbackError.message);
        // Continue processing - don't let callback errors interrupt stream
      }

      // Tool detection (only detect once)
      if (!toolDetected && this._callbacks.detectTool) {
        const toolInfo = this._callbacks.detectTool(decodedChunk);
        if (toolInfo?.detected) {
          toolDetected = true;
          this._callbacks.onToolDetected?.(toolInfo);
        }
      }
    }

    return {
      accumulatedContent,
      chunkCount,
      toolDetected
    };
  }

  /**
   * Clean up resources.
   * Replaces callbacks with no-op functions to prevent errors if methods
   * are called after destroy (defensive programming).
   */
  destroy() {
    this._callbacks = {
      handleSearchTags: () => ({ isInternalOnly: false, reason: SEARCH_TAG_REASONS.DESTROYED }),
      removeSearchTags: (content) => content,
      detectTool: null,
      onToolDetected: null,
      onChunk: null
    };
  }
}

export default ResponseHandler;
